<script setup>
import {defineModel, defineProps, defineEmits, ref, computed, watch} from 'vue'
import TickBlackSvg from "@/components/UI/svg/TickBlackSvg.vue";
import TrashCanSvg from "@/components/UI/svg/TrashCanSvg.vue";
import SelectDropdown from "@/components/UI/SelectDropdown.vue";
import InputFilter from "@/components/UI/InputFilter.vue";
import {isEmpty} from "@/service/utils";
import LoadingSvg from "@/components/UI/LoadingSvg.vue";
import RefreshSvg from "@/components/UI/svg/RefreshSvg.vue";


const emits = defineEmits(['reloadColumns', 'removeCondition'])

const condition = defineModel()

const isRevealed = ref(true)

const criteriaToInputType = {
    NUMBER: "number",
    TEXT: "text",
}

const inputType = ref("")


const isColumnError = computed(() => {
    return isEmpty(condition.value.column)
})

const isCriteriaError = computed(() => {
    return isEmpty(condition.value.criteria)
})

const isInputError = computed(() => {
    return condition.value?.filter_value?.data === ""
})

const isInputDisabled = computed(() => {
    return isEmpty(condition.value.criteria) || condition.value.criteria.criteria_type === "IS_TRUE" || condition.value.criteria.criteria_type === "IS_FALSE"
})

const isDisabledLabelActive = computed(() => {
    return isEmpty(condition.value.criteria)
})

const isInputRequired = computed(() => {
    return !(condition.value.criteria.criteria_type === "IS_TRUE" || condition.value.criteria.criteria_type === "IS_FALSE")
})

watch(() => condition.value.criteria.value_type, (newCriteriaType, oldCriteriaType) => {
    if (newCriteriaType !== oldCriteriaType) {
        if (condition.value.filter_value !== undefined) {
            condition.value.filter_value.data = ""
            condition.value.filter_value.type = newCriteriaType
        }
    }
    if (criteriaToInputType[newCriteriaType] === undefined) {
        return 'text'
    }

    condition.value.filter_value.type = newCriteriaType
    inputType.value = criteriaToInputType[newCriteriaType]
})



const props = defineProps({
    tableColumns: {
        type: Array,
        required: true
    },
    isLoading: {
        type: Boolean,
        required: true,
        default: false
    },
    criteria: {
        type: Array,
        required: true
    }
})


function acceptCondition() {
    if (isEmpty(condition.value.column)) {
        return
    }
    if (isEmpty(condition.value.criteria)) {
        return
    }
    if (condition.value.filter_value.data === "") {
        return
    }

    isRevealed.value = false
}


function reloadColumns() {
    emits('reloadColumns')
}
</script>

<template>
    <div v-if="!isRevealed"
         v-on:click="isRevealed = true"
         class="w-full h-20 flex justify-between items-center bg-gray-100 select-none cursor-pointer hover:bg-gray-200"
    >
        <div class="ml-10">
            <div class="font-bold text-gray-500 text-sm">Колонка</div>
            <div>{{condition.column.column_letter}}</div>
        </div>
        <div>
            <div class="font-bold text-gray-500 text-sm">Условие</div>
            <div>{{condition.criteria.criteria_name}}</div>
        </div>
        <div class="mr-10">
            <div class="font-bold text-gray-500 text-sm">Значение</div>
            <div>{{condition.filter_value.data}}</div>
        </div>
    </div>
    <div v-else class="w-full flex flex-col items-center justify-center bg-gray-100 rounded-lg">
        <div class="w-full flex justify-between px-10 my-4">
            <div class="font-bold">
                Условие
            </div>
            <div v-on:click="reloadColumns" class="font-bold text-xs cursor-pointer text-blue-600 hover:text-blue-700 flex items-center justify-center">
                <div v-if="isLoading">
                    <LoadingSvg class="size-3 mr-2 animate-spin" />
                </div>
                <div v-else>
                    <RefreshSvg class="size-4 mr-2 fill-blue-600"/>
                </div>
                <div>
                    Синхронизировать колонки
                </div>
            </div>
        </div>
        <div class="w-[calc(100%-40px)] flex gap-4 border-b pb-10 border-black">
            <SelectDropdown
                class="w-1/3"
                v-model="condition.column"
                :values="props.tableColumns"
                label="Выберите колонку"
                :format-function="(item) => `Колонка ${item.column_letter}: ${item.column_value}`"
                :is-error="isColumnError"
                :is-loading="props.isLoading"
                is-loading-label="Загружаем колонки"
            />
            <SelectDropdown
                class="w-1/3"
                v-model="condition.criteria"
                :values="props.criteria"
                label="Выберите условие"
                :format-function="(item) => `${item.criteria_name}`"
                :is-error="isCriteriaError"
            />
            <InputFilter
                :is-error="isInputError"
                :type="inputType"
                :is-disabled="isInputDisabled"
                :is-disabled-label-active="isDisabledLabelActive"
                :is-required="isInputRequired"
                v-model="condition.filter_value.data"
            />
        </div>
        <div class="h-20 flex items-center ml-auto mr-10 gap-4">
            <div class="flex items-center justify-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-gray-200 transition-all duration-150"
                 v-on:click="emits('removeCondition')"
            >
                <TrashCanSvg class="size-4 stroke-2 stroke-gray-600" />
                <div class="font-bold text-xs text-gray-600">Удалить</div>
            </div>
            <div class="flex items-center justify-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-gray-200 transition-all duration-150"
                 v-on:click="acceptCondition"
            >
                <TickBlackSvg class="size-4 stroke-blue-600" />
                <div class="font-bold text-xs text-blue-600">Подтвердить</div>
            </div>
        </div>
    </div>

</template>

<style scoped>

</style>